<template>
  <div v-if="products && products.length > 0" class="variant-grid">
    <marquee always class="variant-grid__marquee variant-grid__marquee--top">
      <div
        v-if="marquees && marquees.length > 0"
        class="variant-grid__marquee-text"
      >
        <span
          v-for="(text, i) in marquees"
          :key="`marquee-text-top-${i}`"
          class="inline-block"
          >{{ text }}&nbsp;{{ i % 2 ? '↑' : '←' }}&nbsp;</span
        >
      </div>
      <div v-else class="variant-grid__marquee-text">
        <span
          v-for="(text, i) in slogans"
          :key="`marquee-text-top-${i}`"
          class="inline-block"
          >{{ text }}&nbsp;{{ i % 2 ? '↑' : '←' }}&nbsp;</span
        >
      </div>
    </marquee>
    <div class="variant-grid__grid">
      <div
        v-for="(product, i) in products"
        :key="product._id"
        ref="tracks"
        :class="trackClass(i)"
        class="variant-grid__track"
        tabindex="0"
        role="button"
        @mouseover="over(i)"
        @mouseleave="out(i)"
        @focus="over(i)"
        @blur="out(i)"
      >
        <i
          v-for="j in 4"
          :key="`product-track-${i}-edge-${j}`"
          class="variant-grid__edge"
          aria-hidden="true"
        />
        <variant-thumb
          :float-reverse="i % 2 ? true : false"
          :float-offset="i % 2 ? '-2%' : '2%'"
          :float-speed="i % 2 ? 4 : 3"
          :float-stopped="bp && bp.sm && i !== activeTrack"
          :title="product.title || product.product.title"
          :subtitle="product.subtitle"
          :product="product.product"
          :variant-id="product.variant.id"
          :interval="interval"
          is-subscription
          add-to-cart
          class="variant-grid__thumb"
        />
        <div v-if="bp" class="variant-grid__clouds">
          <drifting-cloud
            :key="`product-cell-${i}-cloud-left`"
            :width="`${cloudsWidth}px`"
            :distance="cloudsDistance"
            :speed="cloudsSpeed"
            :dismissable="false"
            column="1 / 3"
            static
            class="product-grid__cloud h-full"
          />
          <drifting-cloud
            :key="`product-cell-${i}-cloud-right`"
            :width="`${cloudsWidth}px`"
            :distance="cloudsDistance"
            :speed="cloudsSpeed"
            :dismissable="false"
            column="5 / 8"
            static
            class="product-grid__cloud h-full"
          />
        </div>
      </div>
      <div
        v-for="i in emptyTracks"
        :key="`track-${i}`"
        class="variant-grid__track pointer-events-none"
        aria-hidden="true"
      >
        <template v-if="bp">
          <i
            v-for="j in 4"
            :key="`empty-track-${i}-edge-${j}`"
            class="variant-grid__edge"
          />
          <div class="variant-grid__clouds">
            <drifting-cloud
              :key="`empty-cell-${i}-cloud-top-left`"
              :width="`${cloudsWidth}px`"
              :speed="cloudsSpeed"
              :dismissable="false"
              distance="2"
              column="1"
              static
              class="product-grid__cloud product-grid__cloud--empty top-0 h-1/2"
            />
            <drifting-cloud
              :key="`empty-cell-${i}-cloud-top-right`"
              :width="`${cloudsWidth}px`"
              :speed="cloudsSpeed"
              :dismissable="false"
              column="3"
              distance="3"
              static
              class="product-grid__cloud product-grid__cloud--empty top-1/2 h-1/2 transform -translate-y-1/2"
            />
            <drifting-cloud
              :key="`empty-cell-${i}-cloud-bottom-left`"
              :width="`${cloudsWidth}px`"
              :speed="cloudsSpeed"
              :dismissable="false"
              column="2"
              distance="4"
              static
              class="product-grid__cloud product-grid__cloud--empty bottom-0 h-1/4 transform translate-y-1/4"
            />
          </div>
        </template>
      </div>
    </div>
    <marquee
      always
      reverse
      class="variant-grid__marquee variant-grid__marquee--bottom"
    >
      <div
        v-if="marquees && marquees.length > 0"
        class="variant-grid__marquee-text"
      >
        <span
          v-for="(text, i) in marquees"
          :key="`marquee-text-top-${i}`"
          class="inline-block"
          >{{ text }}&nbsp;{{ i % 2 ? '↑' : '←' }}&nbsp;</span
        >
      </div>
      <div v-else class="variant-grid__marquee-text">
        <span
          v-for="(text, i) in slogans"
          :key="`marquee-text-bottom-${i}`"
          class="inline-block"
          >{{ text }}&nbsp;{{ i % 2 ? '↑' : '←' }}&nbsp;</span
        >
      </div>
    </marquee>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { slogans } from '~/recess.json'
import VariantThumb from '~/components/shop/products/variant-thumb.vue'

export default {
  name: 'VariantGrid',

  components: {
    VariantThumb
  },

  props: {
    products: {
      default: () => [],
      type: Array
    },
    marquees: {
      default: undefined,
      type: Array
    },
    interval: {
      default: undefined,
      type: String
    }
  },

  data: () => ({
    colCount: 0,
    emptyTracks: 0,
    cloudsWidth: 0,
    cloudsSpeed: 12,
    cloudsDistance: 4,
    activeTrack: null,
    slogans
  }),

  computed: {
    ...mapGetters('screen', {
      bp: 'breakpoint'
    })
  },

  mounted() {
    this.$nextTick(this.resize)
    this.$bus.$on('resize', this.resize)
  },

  beforeDestroy() {
    this.$bus.$off('resize', this.resize)
  },

  methods: {
    async setup() {
      // force repaint to ensure correct sizes
      await new Promise(resolve => requestAnimationFrame(resolve))
      const tracks = this.$refs.tracks
      if (!tracks || !tracks.length) {
        return
      }
      const totalTracks = tracks.length
      const track = tracks[0]
      const width = this.$el.clientWidth
      const height = this.$el.clientHeight
      const trackWidth = track.clientWidth - 1
      const trackHeight = track.clientHeight - 1
      const colCount = Math.max(1, Math.floor(width / trackWidth))
      const rowCount = Math.max(1, Math.floor(height / trackHeight))
      const remainder = Math.ceil((colCount * rowCount) % totalTracks)
      this.colCount = colCount
      this.cloudsWidth = trackWidth
      this.emptyTracks = Math.max(0, remainder)
    },
    over(index) {
      this.activeTrack = index
    },
    out() {
      this.activeTrack = null
    },
    resize() {
      this.emptyTracks = 0
      this.$nextTick(this.setup)
    },
    trackClass(index) {
      return (index + 1) % this.colCount ? '' : 'is-end'
    }
  }
}
</script>

<style>
.variant-grid {
  --grid-background-color: theme('colors.apricot');
  --grid-text-color: theme('colors.default-text-color');
  color: var(--grid-text-color);
}

.variant-grid__marquee {
  background-color: var(--grid-background-color);
  border-left: 1px solid var(--grid-text-color);
  border-right: 1px solid var(--grid-text-color);
  color: var(--grid-text-color);
  font-family: theme('fontFamily.antidote');
  line-height: 1;
}

.variant-grid__marquee--top {
  margin-bottom: -1px;
}

.variant-grid__marquee--bottom {
  margin-top: -1px;
}

.variant-grid__marquee-text {
  border-bottom: 2px solid var(--grid-text-color);
  border-top: 2px solid var(--grid-text-color);
  padding: 0.75em 0;
  white-space: nowrap;
}

.variant-grid__grid {
  background-color: var(--grid-text-color);
  display: grid;
  grid-auto-rows: 1fr;
  grid-gap: 0;
  grid-template-columns: repeat(
    auto-fit,
    minmax(var(--grid-track-min), var(--grid-track-max))
  );
  grid-template-rows: repeat(
    auto-fit,
    minmax(var(--grid-track-min), var(--grid-track-max))
  );
}

.variant-grid__track {
  background-color: var(--grid-background-color);
  color: var(--grid-text-color);
  display: flex;
  height: 100%;
  position: relative;
  text-align: center;
  transform-style: preserve-3d;
}

.variant-grid__track::before {
  content: '';
  display: block;
  height: 0;
}

.is-scrolling .variant-grid__track {
  pointer-events: none;
}

.variant-grid__edge {
  background-color: var(--grid-text-color);
  content: '';
  display: none;
  position: absolute;
  z-index: 3;
}

/* top edge */
.variant-grid__edge:nth-child(1) {
  display: block;
  height: 2px;
  left: 0;
  top: -1px;
  width: 100%;
}

/* right edge */
.variant-grid__edge:nth-child(2) {
  height: 100%;
  right: -1px;
  top: 0;
  width: 2px;
}

/* bottom edge */
.variant-grid__edge:nth-child(3) {
  bottom: -1px;
  display: block;
  height: 2px;
  left: 0;
  width: 100%;
}

/* left edge */
.variant-grid__edge:nth-child(4) {
  height: 100%;
  left: -1px;
  top: 0;
  width: 2px;
}

.variant-grid__link {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.variant-grid__thumb {
  flex: 1 0 auto;
  min-height: var(--grid-track-min);
  position: relative;
  z-index: 2;
}

.variant-grid .variant-thumb__link {
  padding: var(--grid-track-gutter);
}

.variant-grid .variant-thumb__header {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: var(--grid-track-gutter);
  margin-left: var(--grid-track-gutter);
  margin-right: var(--grid-track-gutter);
}

.variant-grid .variant-thumb__title,
.variant-grid .variant-thumb__subtitle {
  width: 100%;
}

.variant-grid .variant-thumb__subtitle {
  margin-top: 0.375em;
}

.variant-grid__clouds,
.variant-grid__cloud {
  left: 0;
  position: absolute;
  width: 100%;
}

.variant-grid__clouds {
  height: 100%;
  overflow: hidden;
  top: 0;
}

@media (hover) and (min-width: theme('screens.xs')) {
  .variant-grid__track:hover {
    transform: translate(8px, -8px);
    z-index: 4;
  }

  .variant-grid__track.is-end:hover {
    transform: translate(-8px, -8px);
    z-index: 5;
  }
}

@screen xs {
  .variant-grid__track {
    transition: transform var(--hover-speed);
  }

  [data-js-focus-visible] .variant-grid__track[data-focus-visible-added] {
    transform: translate(8px, -8px);
    z-index: 4;
  }

  [data-js-focus-visible]
    .variant-grid__track.is-end[data-focus-visible-added] {
    transform: translate(-8px, -8px);
    z-index: 5;
  }

  .variant-grid__edge {
    display: block;
  }

  .variant-grid .variant-thumb__header {
    min-height: 2.375em;
  }
}
</style>
